/**
 * UpcomingPosts Component
 *
 * Sidebar component showing upcoming posts in chronological order
 */

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

function UpcomingPosts({ posts, onPostClick }) {
  // Filter and sort upcoming posts
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingPosts = posts
    .filter((post) => {
      const postDate = new Date(post.date || post.created_at);
      return postDate >= today && post.status !== "Rejected";
    })
    .sort((a, b) => {
      const dateA = new Date(a.date || a.created_at);
      const dateB = new Date(b.date || b.created_at);
      return dateA - dateB;
    })
    .slice(0, 10); // Show only next 10 posts

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays < 7) return `In ${diffDays} days`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Posted":
        return "success";
      case "Pending Approval":
      case "Good to go":
        return "warning";
      case "Rejected":
        return "error";
      default:
        return "info";
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: { xs: "300px", md: "400px" },
        borderRadius: "16px",
        boxShadow: ({ boxShadows }) => boxShadows.lg,
      }}
    >
      <MDBox p={{ xs: 2, sm: 2.5, md: 3 }}>
        <MDBox display="flex" alignItems="center" mb={2}>
          <Icon fontSize="medium" sx={{ mr: 1 }}>
            event_upcoming
          </Icon>
          <MDTypography variant="h6" fontWeight="medium">
            Upcoming Posts
          </MDTypography>
        </MDBox>

        <Divider />

        {upcomingPosts.length === 0 ? (
          <MDBox py={3} textAlign="center">
            <Icon fontSize="large" color="disabled" sx={{ fontSize: "3rem", mb: 1 }}>
              event_busy
            </Icon>
            <MDTypography variant="body2" color="text" mt={1}>
              No upcoming posts scheduled
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox
            mt={2}
            sx={{
              maxHeight: { xs: "250px", md: "500px" },
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {upcomingPosts.map((post, index) => (
              <MDBox key={post.id}>
                <MDBox
                  onClick={() => onPostClick(post)}
                  sx={{
                    cursor: "pointer",
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: "12px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                    "&:hover": {
                      backgroundColor: ({ palette }) => palette.grey[100],
                      transform: "translateX(4px)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="start" mb={0.5}>
                    <MDTypography variant="button" fontWeight="medium" sx={{ flex: 1 }}>
                      {post.title || "Untitled Post"}
                    </MDTypography>
                    <MDBadge
                      badgeContent={post.status}
                      color={getStatusColor(post.status)}
                      size="xs"
                      sx={{ ml: 1 }}
                    />
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small" sx={{ fontSize: "0.875rem" }}>
                      schedule
                    </Icon>
                    <MDTypography variant="caption" color="text">
                      {formatDate(post.date || post.created_at)}
                    </MDTypography>
                  </MDBox>

                  {/* Platform indicators */}
                  <MDBox display="flex" gap={0.5} mt={0.5}>
                    {(post.linkedin_post_url || post.linkedinCopy) && (
                      <Icon fontSize="small" color="info" sx={{ fontSize: "0.875rem" }}>
                        work
                      </Icon>
                    )}
                    {(post.x_post_url || post.xCopy) && (
                      <MDTypography variant="caption" fontWeight="bold" color="dark">
                        𝕏
                      </MDTypography>
                    )}
                    {(post.threads_post_url || post.threadsCopy) && (
                      <Icon fontSize="small" color="secondary" sx={{ fontSize: "0.875rem" }}>
                        forum
                      </Icon>
                    )}
                  </MDBox>
                </MDBox>
                {index < upcomingPosts.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </MDBox>
            ))}
          </MDBox>
        )}

        {upcomingPosts.length > 0 && (
          <MDBox mt={2}>
            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              fullWidth
              sx={{
                minHeight: { xs: "44px", sm: "36px" },
                fontSize: { xs: "0.8rem", sm: "0.75rem" },
              }}
            >
              View All Posts
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

UpcomingPosts.propTypes = {
  posts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      status: PropTypes.string.isRequired,
      date: PropTypes.string,
      created_at: PropTypes.string,
      linkedin_post_url: PropTypes.string,
      x_post_url: PropTypes.string,
      threads_post_url: PropTypes.string,
      linkedinCopy: PropTypes.string,
      xCopy: PropTypes.string,
      threadsCopy: PropTypes.string,
    })
  ).isRequired,
  onPostClick: PropTypes.func.isRequired,
};

export default UpcomingPosts;
